// frontend/pages/login.js — clean, traditional login matching the 1PARC email
// style (white card, blue accents). No animation. Robust password field.
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { AlertTriangle, Mail, Loader2, User, Lock } from 'lucide-react';
import API_URL from '../api/baseURL';
import { ReactComponent as Logo } from '../assets/logo.svg';

const BLUE = '#3b82f6';
const BLUE_DARK = '#2563eb';
const TEXT = '#334155';
const MUTED = '#64748b';
const BORDER = '#e2e8f0';

const Login = () => {
  const [form, setForm]                   = useState({ email: '', password: '' });
  const [error, setError]                 = useState('');
  const [loading, setLoading]             = useState(false);
  const [needsVerification, setNeedsV]    = useState(false);
  const [resending, setResending]         = useState(false);
  const [showPassword, setShowPassword]   = useState(false);

  // Use a plain, standard password field. A previous version masked the field
  // with -webkit-text-security on a type=text input to hide Android's native
  // reveal "eye" — but that is a documented cause of Android keyboards (GBoard)
  // failing to type into the field (users could only type after rotating to
  // landscape, which uses the OS full-screen editor). A normal type=password is
  // handled correctly by every keyboard; the native reveal control is hidden
  // via CSS below, and show/hide is driven by the checkbox under the field.
  const passwordType = showPassword ? 'text' : 'password';

  const navigate = useNavigate();
  const { login } = useAuth();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true); setNeedsV(false);
    try {
      const res   = await axios.post(`${API_URL}/auth/login`, form);
      const token = res.data.token;
      login(token);
      let decoded;
      try { decoded = JSON.parse(atob(token.split('.')[1])); }
      catch { setError('Token reçu invalide. Contactez le support.'); return; }
      const role = decoded.role?.toLowerCase();
      if      (role === 'car owner')             navigate('/dashboard');
      else if (role === 'car workshop')          navigate('/workshop');
      else if (role === 'spare parts supplier')  navigate('/supplier');
      else if (role === 'insurance company')     navigate('/insurance');
      else if (role === 'vehicle loader')        navigate('/loader');
      else if (role === 'admin')                 navigate('/admin/dashboard');
      else                                       navigate('/');
    } catch (err) {
      const msg = err.response?.data?.message || 'Échec de la connexion';
      setError(msg);
      if (err.response?.status === 403 && err.response?.data?.needsVerification) {
        setNeedsV(true);
      }
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API_URL}/auth/resend-verification`, { email: form.email });
      alert('Un nouveau lien de confirmation a été envoyé à votre adresse e-mail.');
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de l'envoi.");
    } finally { setResending(false); }
  };

  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: TEXT, marginBottom: 6 };
  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: '12px 14px',
    fontSize: 16, // 16px avoids iOS auto-zoom on focus
    color: TEXT, background: '#fff',
    border: `1px solid ${BORDER}`, borderRadius: 8,
    outline: 'none', transition: 'border-color .15s, box-shadow .15s',
  };

  return (
    <div dir="ltr" style={{
      minHeight: '100vh', width: '100%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f1f5f9', padding: '24px 16px',
      fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      direction: 'ltr', textAlign: 'left',
    }}>
      <style>{`
        .lg-input:focus { border-color:${BLUE} !important; box-shadow:0 0 0 3px rgba(59,130,246,.15) !important; }
        .lg-btn:hover:not(:disabled) { background:${BLUE_DARK} !important; }
        .lg-link:hover { text-decoration:underline; }
        /* belt-and-suspenders: hide every native reveal / clear / autofill
           control across engines so nothing overlaps the typing area. */
        input::-ms-reveal, input::-ms-clear { display:none !important; }
        input::-webkit-credentials-auto-fill-button,
        input::-webkit-strong-password-auto-fill-button,
        input::-webkit-contacts-auto-fill-button,
        input::-webkit-caps-lock-indicator {
          visibility:hidden !important; display:none !important;
          pointer-events:none !important; width:0 !important; margin:0 !important;
        }
      `}</style>

      <div style={{
        width: '100%', maxWidth: 420,
        background: '#fff', border: `1px solid ${BORDER}`,
        borderRadius: 16, boxShadow: '0 10px 40px rgba(15,23,42,.08)',
        padding: '36px 28px 28px',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <Logo style={{ height: 30 }} />
            <span style={{ fontSize: 24, fontWeight: 800, color: BLUE, letterSpacing: '.5px' }}>1PARC</span>
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: TEXT, margin: 0 }}>Connexion à votre compte</h1>
          <p style={{ fontSize: 14, color: MUTED, marginTop: 6 }}>Accédez à votre espace 1PARC</p>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
            borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 16,
          }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}

        {/* Resend verification */}
        {needsVerification && (
          <button
            type="button" onClick={handleResend} disabled={resending}
            style={{
              width: '100%', marginBottom: 16, padding: '10px 12px',
              background: '#eff6ff', border: '1px solid #bfdbfe', color: BLUE_DARK,
              borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <Mail size={15} />
            {resending ? 'Envoi…' : 'Renvoyer le lien de confirmation'}
          </button>
        )}

        <form onSubmit={handleSubmit}>
          {/* Email or phone */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Email ou téléphone</label>
            <div style={{ position: 'relative' }}>
              <User size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: MUTED, pointerEvents: 'none' }} />
              <input
                name="email" type="text"
                autoCapitalize="off" autoCorrect="off" spellCheck={false}
                placeholder="Email ou numéro de téléphone"
                value={form.email} onChange={handleChange} required
                autoComplete="username"
                className="lg-input"
                style={{ ...inputStyle, paddingLeft: 38 }}
              />
            </div>
          </div>

          {/* Password — SAME structure as the (working) Email field above: a
              left icon + plain full-width input, with NO in-field toggle. The
              show/hide control is a normal checkbox BELOW the field, so nothing
              can overlap the typing area or be mirrored by iOS. */}
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Mot de passe</label>
            <div style={{ position: 'relative' }}>
              <Lock size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: MUTED, pointerEvents: 'none' }} />
              <input
                name="secret-field" id="lg-secret"
                type={passwordType}
                autoCapitalize="off" autoCorrect="off" spellCheck={false}
                autoComplete="off"
                placeholder="Entrer le mot de passe"
                value={form.password}
                onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                required
                className="lg-input"
                style={{ ...inputStyle, paddingLeft: 38 }}
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, color: MUTED, cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
                style={{ width: 15, height: 15, accentColor: BLUE, cursor: 'pointer' }}
              />
              Afficher le mot de passe
            </label>
          </div>

          {/* Forgot password */}
          <div style={{ textAlign: 'right', marginBottom: 22 }}>
            <Link to="/forgot-password" className="lg-link" style={{ color: BLUE, fontSize: 13, textDecoration: 'none', fontWeight: 500 }}>
              Mot de passe oublié ?
            </Link>
          </div>

          {/* Submit */}
          <button
            type="submit" disabled={loading}
            className="lg-btn"
            style={{
              width: '100%', padding: '13px', background: BLUE, color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'background .15s',
            }}
          >
            {loading && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
            {loading ? 'Connexion…' : 'Se connecter'}
          </button>
        </form>

        {/* Sign up */}
        <p style={{ textAlign: 'center', fontSize: 14, color: MUTED, marginTop: 22 }}>
          Pas encore de compte ?{' '}
          <span onClick={() => navigate('/signup')} className="lg-link" style={{ color: BLUE, cursor: 'pointer', fontWeight: 600 }}>
            Créer un compte
          </span>
        </p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default Login;
